import { ModalBottomButtons, TextArea } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { accountHasPerson } from 'components/Persons/utils/personUtils';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { t } from 'i18next';
import { MsgType } from 'matrix-js-sdk';
import type { RoomMessageEventContent } from 'matrix-js-sdk/lib/types';
import { useMatrixClient } from '../../../hooks/matrix/useMatrixClient';
import { backendApi } from '../../../utils/backendApi';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';

export const GroupMessageFormFragment = graphql(`
  fragment GroupMessageFields on Query {
    group: groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      viewers {
        uuid
        __typename
        user {
          __typename
          uuid
          status
        }
      }
      editors {
        uuid
        __typename
        user {
          __typename
          uuid
          status
        }
      }
    }
  }
`);

type GroupMessageFormProps = {
  groupUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
  query: FragmentType<typeof GroupMessageFormFragment> | undefined;
};

type GroupMessageFormType = {
  text: string;
};

export const GroupMessageForm = ({ groupUuid, onLoad, onClose, query }: GroupMessageFormProps) => {
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const fragment = getFragmentData(GroupMessageFormFragment, query);

  const currentGroup = fragment?.group[0];
  const viewers = currentGroup?.viewers ?? [];
  const editors = currentGroup?.editors ?? [];

  const receiverUuids = new Set(
    [...viewers, ...editors]
      .filter((p) => accountHasPerson(p.user?.status) && p.uuid !== profileUuid)
      .map((p) => p.uuid),
  );

  const { client: matrix } = useMatrixClient();

  const initialValues: GroupMessageFormType = {
    text: '',
  };

  const handleSubmit = async (values: GroupMessageFormType, helper: FormikHelpers<GroupMessageFormType>) => {
    if (!values.text || values.text === '') {
      helper.setFieldError('text', t('validation.required.text'));
      return;
    }

    onLoad(true);

    const content: RoomMessageEventContent = {
      body: values.text,
      formatted_body: values.text,
      msgtype: MsgType.Text,
    };

    const promises = [...receiverUuids.keys()].map((uuid) => {
      return backendApi.createMatrixDm(uuid);
    });

    const responses = await Promise.all(promises);

    const successful = responses.every((response) => response.ok);

    if (successful) {
      const sendPromises = responses.map(async (r) => {
        const json = await r.json();
        if (json.success && json.matrixRoomId) {
          const matrixRoomId = json.matrixRoomId;
          if (matrixRoomId) {
            return matrix?.sendMessage(matrixRoomId, content);
          }
        }
      });

      const sendResponses = await Promise.all(sendPromises);
      const successfulSent = sendResponses.every((response) => response && response.event_id);

      if (successfulSent) {
        showSuccessToast(t('messages.messageSent'));
      } else {
        showErrorToast(t('common.error'));
      }
    } else {
      showErrorToast(t('common.error'));
    }

    helper.resetForm();
    onLoad(false);
    onClose();
  };

  return (
    <Formik<GroupMessageFormType> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, handleChange, errors }) => (
        <Form>
          <TextArea
            label={t('messages.title', { count: 1 })}
            rows={6}
            isScrollable
            name='text'
            onChange={handleChange}
            value={values.text}
            error={errors.text}
          />
          <ModalBottomButtons
            additionalLeftComponent={t('messages.receiverCount', { receiverCount: receiverUuids.size })}
            closeButton={{ callback: onClose }}
            submitButton={{
              text: t('messages.send'),
              disabled: receiverUuids.size === 0,
            }}
          />
        </Form>
      )}
    </Formik>
  );
};
